'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import classes from './Footer.module.scss';
import {
  IconBrandLinkedin,
  IconMail,
  IconPhone,
} from '@tabler/icons-react';
import Cookies from 'js-cookie';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';
import { parseJwt } from '@/src/helpers/decode-jwt';
import { gaCta, gaExternalLink } from '@/src/helpers/gtag';
import { HestiaLogo } from '../HeaderMenu/HestiaLogo/HestiaLogo';
import MenuLink from '../MenuLink/MenuLink';
import { FooterEditorModal } from './FooterEditorModale';

export function Footer() {
  const dictionary: any = useDictionary();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    const token = Cookies.get('authToken');
    if (token) {
      try {
        const decoded = token ? parseJwt(token) : null;
        if (decoded.role === 'superAdmin' || decoded.role === 'admin') {
          setIsSuperAdmin(true);
        }
      } catch (err) {
        console.error('Invalid token', err);
      }
    }
  }, []);

  // Flatten all footer links into a single conversion-focused list
  const quickLinks = dictionary.footer.links
    .flatMap((group: any) => group.links)
    .map((link: any, index: number) => (
      <MenuLink
        key={index}
        className={classes.quickLink}
        href={link.link}
        onClick={() => gaCta(link.label, 'footer_quick_link', link.link)}
      >
        {link.label}
      </MenuLink>
    ));

  const phoneNumber =
    dictionary.whatsappPhone || dictionary.metadata?.organization?.phone || '+33 6 49 34 96 11';
  const phoneHref = phoneNumber.replace(/\s/g, '').replace(/^0/, '+33');

  return (
    <footer className={classes.footer} role="contentinfo">
      {isSuperAdmin && <FooterEditorModal />}

      <div className={classes.mainBand}>
        <div className={classes.container}>
          <div className={classes.mainGrid}>
            <div className={classes.brandColumn}>
              <MenuLink
                href="/"
                className={classes.brandLink}
                aria-label={dictionary.compagnyName}
                onClick={() => gaCta(dictionary.compagnyName, 'footer_brand', '/')}
              >
                <HestiaLogo />
                <div className={classes.brandText}>
                  <span className={classes.brandName}>{dictionary.compagnyName}</span>
                  <span className={classes.brandTagline}>AI systems & web</span>
                </div>
              </MenuLink>
              <p className={classes.description}>{dictionary.footer.description}</p>
              {dictionary.footer.humanNote && (
                <p className={classes.humanNote}>{dictionary.footer.humanNote}</p>
              )}
              <div className={classes.social}>
                {dictionary.metadata?.organization?.sameAs?.find((url: string) =>
                  url.includes('linkedin')
                ) && (
                  <Link
                    href={dictionary.metadata.organization.sameAs.find((url: string) =>
                      url.includes('linkedin')
                    )}
                    className={classes.socialLink}
                    aria-label="LinkedIn"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      gaExternalLink(
                        dictionary.metadata.organization.sameAs.find((url: string) =>
                          url.includes('linkedin')
                        ),
                        'LinkedIn'
                      )
                    }
                  >
                    <IconBrandLinkedin size={18} />
                  </Link>
                )}
              </div>
            </div>

            <div className={classes.contactColumn}>
              <h3 className={classes.contactTitle}>Discutons de votre projet</h3>
              <p className={classes.contactLead}>Vous avez une idée ? Échangeons ensemble.</p>
              <div className={classes.contactActions}>
                <MenuLink
                  href="/contact"
                  className={classes.contactCta}
                  onClick={() => gaCta('Nous écrire', 'footer_contact_page', '/contact')}
                >
                  <IconMail size={16} aria-hidden="true" />
                  Nous écrire
                </MenuLink>
                <a
                  href={`tel:${phoneHref}`}
                  className={classes.contactPhone}
                  aria-label={`Appeler le ${phoneNumber}`}
                  onClick={() => gaExternalLink(`tel:${phoneHref}`, 'Appeler')}
                >
                  <IconPhone size={16} aria-hidden="true" />
                  <span>{phoneNumber}</span>
                </a>
              </div>
            </div>
          </div>

          <nav className={classes.quickLinks} aria-label="Liens du pied de page">
            {quickLinks}
          </nav>
        </div>
      </div>

      <div className={classes.creditBand}>
        <div className={classes.container}>
          <div className={classes.creditGrid}>
            <Link
              href={dictionary.metadata?.url || 'https://www.averiane.com'}
              className={classes.creditLink}
              onClick={() =>
                gaExternalLink(
                  dictionary.metadata?.url || 'https://www.averiane.com',
                  'UI/UX crafted in Paris'
                )
              }
            >
              UI/UX crafted in Paris
            </Link>
            <Link
              href="/sitemap.xml"
              className={classes.creditLink}
              onClick={() => gaCta(dictionary.footer.sitemap, 'footer_quick_link', '/sitemap.xml')}
            >
              {dictionary.footer.sitemap}
            </Link>
            <p className={classes.copyright}>
              © {new Date().getFullYear()} {dictionary.compagnyName} —{' '}
              {dictionary.footer.copyright}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
